import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '@/lib/supabase';
import Logger from "@/lib/logger";

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// API URL for Supabase Edge Functions
const apiUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || 'https://benspipmjclurfwycfsl.supabase.co/functions/v1';

export async function redirectToCheckout(priceId: string, userId: string) {
  try {
    // Get the current session
    const { data: { session } } = await supabase.auth.getSession();
    
    // Get access token
    const accessToken = session?.access_token;
    
    if (!accessToken) {
      throw new Error('User not authenticated');
    }
    
    // Log the request for debugging
    Logger.info(`Creating checkout session for user: ${userId}`);
    
    const response = await fetch(`${apiUrl}/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        userId,
        successUrl: `${window.location.origin}/premium/success`,
        cancelUrl: `${window.location.origin}/premium/failed?reason=cancelled`,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      Logger.error('Checkout error details:', errorData);
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const { url } = await response.json();
    
    if (!url) {
      throw new Error('No checkout URL returned from server');
    }
    
    Logger.info(`Redirecting to checkout URL: ${url.substring(0, 50)}...`);
    window.location.href = url;
  } catch (error) {
    Logger.error('Error redirecting to checkout:', error);
    throw error;
  }
}

export async function redirectToCustomerPortal() {
  try {
    // Get the current session
    const { data } = await supabase.auth.getSession();
    
    if (!data.session) {
      throw new Error('No active session found');
    }
    
    // Get access token
    const accessToken = data.session.access_token;
    
    if (!accessToken) {
      throw new Error('User not authenticated');
    }
    
    Logger.info("Sending request with token:", accessToken.substring(0, 10) + '...');
    
    const response = await fetch(`${apiUrl}/customer-portal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        // Include user ID explicitly in request body as fallback
        userId: data.session.user?.id
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      Logger.error('Customer portal error details:', errorData);
      throw new Error(errorData.error || 'Failed to create portal session');
    }

    const { url } = await response.json();
    window.location.href = url;
  } catch (error) {
    Logger.error('Error redirecting to customer portal:', error);
    throw error;
  }
}

export { stripePromise }; 